import React, { useState } from 'react';
import { useTranslation } from 'localization';
import { PublisherPermissions } from 'types/publisher';
import './ManagerReviewBarMolecule.scss';

export interface ManagerReviewBarMoleculeProps {
  permissions: PublisherPermissions | null | undefined;
  publisherStatus: 'draft' | 'pending_review' | 'published' | 'rejected';
  /** Discriminates between publisher and channel review contexts. Defaults to 'publisher'. */
  entityKind?: 'publisher' | 'channel';
  onApprove?: () => void;
  onReject?: (note: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  className?: string;
}

const ManagerReviewBarMolecule: React.FC<ManagerReviewBarMoleculeProps> = ({
  permissions,
  publisherStatus,
  entityKind: _entityKind = 'publisher', // used in Phase 3 to branch i18n keys
  onApprove,
  onReject,
  approveLoading = false,
  rejectLoading = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // Fail-closed: never render privileged UI when permissions is absent or canApprove is false
  if (!permissions?.canApprove) return null;
  if (publisherStatus !== 'pending_review') return null;

  const classes = ['marketplace-manager-review-bar', className].filter(Boolean).join(' ');

  const handleRejectSubmit = () => {
    if (!rejectNote.trim()) return;
    onReject?.(rejectNote.trim());
  };

  return (
    <div className={classes} role="region" aria-label={t('marketplace.publishers.reviewBar.ariaLabel')}>
      <div className="marketplace-manager-review-bar__header">
        <i className="pi pi-shield marketplace-manager-review-bar__icon" aria-hidden="true" />
        <span className="marketplace-manager-review-bar__title">
          {t('marketplace.publishers.reviewBar.title')}
        </span>
      </div>
      {!showRejectForm ? (
        <div className="marketplace-manager-review-bar__actions">
          <button
            type="button"
            className="marketplace-manager-review-bar__approve-btn"
            onClick={onApprove}
            disabled={approveLoading || rejectLoading}
          >
            {approveLoading ? (
              <i className="pi pi-spinner pi-spin" aria-hidden="true" />
            ) : (
              <i className="pi pi-check" aria-hidden="true" />
            )}
            {t('marketplace.publishers.reviewBar.approve')}
          </button>
          <button
            type="button"
            className="marketplace-manager-review-bar__reject-btn"
            onClick={() => setShowRejectForm(true)}
            disabled={approveLoading || rejectLoading}
          >
            <i className="pi pi-times" aria-hidden="true" />
            {t('marketplace.publishers.reviewBar.reject')}
          </button>
        </div>
      ) : (
        <div className="marketplace-manager-review-bar__reject-form">
          <label
            htmlFor="marketplace-reject-note"
            className="marketplace-manager-review-bar__reject-label"
          >
            {t('marketplace.publishers.reviewBar.rejectNoteLabel')}
          </label>
          <textarea
            id="marketplace-reject-note"
            className="marketplace-manager-review-bar__reject-textarea"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t('marketplace.publishers.reviewBar.rejectNotePlaceholder')}
            rows={3}
          />
          <div className="marketplace-manager-review-bar__reject-form-actions">
            <button
              type="button"
              className="marketplace-manager-review-bar__reject-submit-btn"
              onClick={handleRejectSubmit}
              disabled={!rejectNote.trim() || rejectLoading}
            >
              {rejectLoading ? (
                <i className="pi pi-spinner pi-spin" aria-hidden="true" />
              ) : null}
              {t('marketplace.publishers.reviewBar.rejectConfirm')}
            </button>
            <button
              type="button"
              className="marketplace-manager-review-bar__cancel-btn"
              onClick={() => { setShowRejectForm(false); setRejectNote(''); }}
            >
              {t('marketplace.publishers.reviewBar.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

ManagerReviewBarMolecule.displayName = 'ManagerReviewBarMolecule';

export default ManagerReviewBarMolecule;
